import React, { useState, useEffect, useCallback, useRef } from 'react';
import { InfoCard } from '@backstage/core-components';
import {
  styled,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Box,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { ContentCopyIcon } from '../../icons/ContentCopyIcon';
import { enqueueSnackbar } from 'notistack';
import { Credentials } from '@app/shared/types';
import { Confirm } from './Confirm';
import { EditOffIcon } from '../../icons/EditOffIcon';
import { useProjectEnvMap } from '../../../shared/hooks/useProjectEnvMap';
import { useTheme } from '@material-ui/core/styles';

const StyledInfoCard = styled(InfoCard)(({ theme }) => ({
  border: `1px solid ${theme.palette.border} !important`,
  boxShadow: 'none',

  '& [class^=MuiCardContent]': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  '& [class^=MuiCardHeader-root]': {
    padding: '8px 16px',
  },
}));

export interface CredentialsInputGroupProps {
  data: Credentials;
  title: string;
  disabled?: boolean;
  hasEdit?: boolean;
  hasDelete?: boolean;
  isNew?: boolean;
  shouldValidate?: boolean;
  onEdit?: (isEditing: boolean) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  onChange?: (data: Credentials) => void;
  usedByProject?: Record<string, Set<string>>;
}

export const CredentialsInputGroup = ({
  data,
  title,
  hasEdit,
  hasDelete = false,
  isNew = false,
  shouldValidate = false,
  onEdit,
  onCancel,
  onDelete,
  onChange,
  usedByProject,
}: CredentialsInputGroupProps) => {
  const theme = useTheme();
  const projectEnvMap = useProjectEnvMap();
  const [isLocalEditing, setIsLocalEditing] = useState(isNew);
  const [localData, setLocalData] = useState<Credentials>(data);
  const [originalData, setOriginalData] = useState<Credentials>(data);
  const [errors, setErrors] = useState<Record<keyof Credentials, boolean>>({
    name: false,
    clientId: false,
    clientSecret: false,
    project: false,
    env: false,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedEnv, setSelectedEnv] = useState<string>('');
  const isUserChangeRef = useRef(false);
  const prevDataRef = useRef<Credentials>(data);

  useEffect(() => {
    isUserChangeRef.current = false;
    prevDataRef.current = data;
    setLocalData(data);
    if (!isLocalEditing) {
      setOriginalData(data);
    }
  }, [data, isLocalEditing]);

  // Sync localData
  useEffect(() => {
    if (isUserChangeRef.current && onChange) {
      onChange(localData);
      isUserChangeRef.current = false;
    }
  }, [localData, onChange]);

  useEffect(() => {
    if (isNew) {
      setIsLocalEditing(true);
      setErrors({
        name: false,
        clientId: false,
        clientSecret: false,
        project: false,
        env: false,
      });
    }
  }, [isNew]);

  const handleCopy = (value?: string) => {
    if (value) {
      navigator.clipboard.writeText(value);
      enqueueSnackbar('Copied to clipboard', {
        variant: 'success',
      });
    }
  };

  const handleEdit = () => {
    if (isLocalEditing) {
      setLocalData(originalData);
      setIsLocalEditing(false);
      onCancel?.();
      onEdit?.(false);
    } else {
      setOriginalData(localData);
      setIsLocalEditing(true);
      onEdit?.(true);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete?.();
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const toggleClientSecretVisibility = () => {
    setShowClientSecret(prev => !prev);
  };

  const validateField = (field: keyof Credentials, value: string) => {
    const isEmpty = !value?.trim();
    setErrors(prev => ({ ...prev, [field]: isEmpty }));
    return !isEmpty;
  };

  const validateAllFields = useCallback(() => {
    const clientIdValid = validateField('clientId', localData.clientId);
    const clientSecretValid = validateField(
      'clientSecret',
      localData.clientSecret,
    );

    if (isNew) {
      setErrors(prev => ({
        ...prev,
        project: !selectedProject,
        env: !selectedEnv,
      }));

      return (
        !!selectedProject && !!selectedEnv && clientIdValid && clientSecretValid
      );
    }

    const nameValid = validateField('name', localData.name);
    return nameValid && clientIdValid && clientSecretValid;
  }, [
    isNew,
    selectedProject,
    selectedEnv,
    localData.name,
    localData.clientId,
    localData.clientSecret,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      validateAllFields();
    }
  }, [shouldValidate, validateAllFields]);

  const handleChange =
    (field: keyof Credentials) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const newData = { ...localData, [field]: value };
      isUserChangeRef.current = true;
      setLocalData(newData);
      validateField(field, value);
    };

  const projects = Object.keys(projectEnvMap || {});
  const projectDisabled = projects.length <= 1;
  const envOptions = selectedProject
    ? projectEnvMap?.[selectedProject] || []
    : [];
  const usedSetForProject =
    selectedProject && usedByProject
      ? usedByProject[selectedProject] || new Set<string>()
      : new Set<string>();

  const updateCredentialsWithProjectEnv = useCallback(
    (project: string, env: string) => {
      const newName = `${project} ${env}`;
      isUserChangeRef.current = true;
      setLocalData(prevData => ({
        ...prevData,
        project,
        env,
        name: newName,
      }));
    },
    [],
  );

  const handleProjectChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const project = String(event.target.value || '');
    const env = '';
    setErrors(prev => ({ ...prev, project: false, env: false }));
    setSelectedProject(project);
    setSelectedEnv(env);
    updateCredentialsWithProjectEnv(project, env);
  };

  const handleEnvChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const env = String(event.target.value || '');
    setErrors(prev => ({ ...prev, env: false }));
    setSelectedEnv(env);
    updateCredentialsWithProjectEnv(selectedProject, env);
  };

  const getEnvHelperText = () => {
    if (errors.env) return 'Required';
    if (!selectedProject) return 'Please select a name first';
    return undefined;
  };

  const showActions = hasEdit || isNew || hasDelete;

  const Title = (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        style={{
          fontSize: '18px',
          margin: 0,
          fontFamily: 'Red Hat Display',
          letterSpacing: '0.15px',
        }}
      >
        {title || data?.name}
      </Typography>
      {showActions && (
        <Box display="flex" style={{ gap: 8 }}>
          {hasEdit && (
            <IconButton
              size="small"
              aria-label={
                isLocalEditing ? 'cancel editing' : 'edit credentials'
              }
              onClick={handleEdit}
            >
              {isLocalEditing ? <EditOffIcon /> : <EditIcon color="primary" />}
            </IconButton>
          )}
          {hasDelete && (
            <IconButton
              size="small"
              aria-label="delete credentials"
              onClick={() => handleDelete()}
            >
              <DeleteIcon color="error" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <StyledInfoCard title={Title}>
        {isNew ? (
          <>
            <TextField
              select
              variant="outlined"
              label="Name"
              value={selectedProject || ''}
              onChange={e => handleProjectChange(e as any)}
              disabled={projectDisabled}
              required
              error={!!errors.project}
              {...(errors.project && { helperText: 'Required' })}
              placeholder={!selectedProject ? 'Name' : ''}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: val =>
                  val ? (
                    String(val)
                  ) : (
                    <span style={{ color: theme.palette.text.secondary }}>
                      Name
                    </span>
                  ),
              }}
            >
              {projects.map(project => (
                <MenuItem key={project} value={project}>
                  {project}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              variant="outlined"
              label="Env"
              value={selectedEnv || ''}
              onChange={e => handleEnvChange(e as any)}
              disabled={!selectedProject}
              required
              error={!!errors.env}
              {...(getEnvHelperText() && { helperText: getEnvHelperText() })}
              placeholder={!selectedEnv ? 'Env' : ''}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: val =>
                  val ? (
                    String(val)
                  ) : (
                    <span style={{ color: theme.palette.text.secondary }}>
                      Env
                    </span>
                  ),
              }}
            >
              {envOptions.map(env => (
                <MenuItem
                  key={env}
                  value={env}
                  disabled={usedSetForProject.has(env)}
                >
                  {env}
                </MenuItem>
              ))}
            </TextField>
          </>
        ) : null}
        <TextField
          variant="outlined"
          value={localData.clientId}
          onChange={handleChange('clientId')}
          disabled={!isLocalEditing}
          placeholder="Client ID"
          required
          label="Client ID"
          error={errors.clientId}
          helperText={errors.clientId ? 'Required' : ''}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: isLocalEditing ? null : (
              <InputAdornment position="end">
                <IconButton
                  aria-label="copy client ID"
                  onClick={() => handleCopy(localData.clientId)}
                >
                  <ContentCopyIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          variant="outlined"
          type={showClientSecret ? 'text' : 'password'}
          value={
            showClientSecret || isLocalEditing
              ? localData.clientSecret
              : '••••••••••••••••'
          }
          onChange={handleChange('clientSecret')}
          disabled={!isLocalEditing}
          placeholder="Client secret"
          required
          label="Client secret"
          error={errors.clientSecret}
          helperText={errors.clientSecret ? 'Required' : ''}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showClientSecret ? 'hide password' : 'show password'
                  }
                  onClick={toggleClientSecretVisibility}
                >
                  {showClientSecret ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
                {!isLocalEditing && (
                  <IconButton
                    aria-label="copy client secret"
                    onClick={() => handleCopy(localData.clientSecret)}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </StyledInfoCard>
      <Confirm
        isOpen={showDeleteConfirm}
        title="Delete Client Credentials"
        content={`Are you sure you want to delete the credentials "${
          localData.name || 'Unnamed'
        }"? This action cannot be undone.`}
        confirm="Delete"
        cancel="Cancel"
        onConfirm={confirmDelete}
        onClose={cancelDelete}
      />
    </>
  );
};
